import { getDefinition, getInstanceDefinition } from '../cards/cardLookup';
import { isHeroLike, grantXp, destroyFromHand } from '../cards/effectHelpers';

// Numenera, Strong: "You cannot level up a Regular into this card." The
// only hero group with this restriction, so a small explicit set rather
// than a generic per-card flag threaded through the whole leveling path.
const NO_REGULAR_LEVEL_UP_GROUPS = new Set(['strong']);

// Into the Abyss: "The level 4 heroes only have one copy each. The only way
// to level up from 3 to 4 is to win a combat with a level 3 in your hand.
// Then you can level up to 4 for 4xp as a spoils ability." (Matt, confirming
// the mechanism directly - not the normal Village LEVEL_UP_HERO path used
// for every other level-up step in the game, including this same group's
// own 1->2 and 2->3 steps.) Detected generically off the existence of a
// level4 definition for the group, rather than a hardcoded list of the 10
// ItA group keys - any future 4-level group would get the same treatment
// automatically.
function hasLevel4(group) {
  try {
    return getDefinition(`hero-${group}-l4`).level === 4;
  } catch {
    return false;
  }
}

// Every level-1 hero currently sitting on top of its group's village stack -
// i.e. every legal target for leveling up a Regular (rulebook, "Level Up":
// "You can level a Regular into any level 1 hero available"). Also used by
// the UI to render the choice. No "already leveled this turn" filtering
// needed here: these are always fresh village-stack cards that can't
// already be a result of a level-up performed this turn (see
// performLevelUp - a leveled-into card always lands in the player's own
// hand/discard, never back in state.village.heroStacks).
export function getRegularLevelUpCandidates(state) {
  const candidates = [];
  for (const [groupKey, stack] of Object.entries(state.village.heroStacks)) {
    if (NO_REGULAR_LEVEL_UP_GROUPS.has(groupKey)) continue;
    const topInstanceId = stack[0];
    if (!topInstanceId) continue;
    const def = getInstanceDefinition(state, topInstanceId);
    if (def.level !== 1) continue;
    candidates.push({ topInstanceId, cost: getDefinition(`hero-${groupKey}-l1`)?.cost ?? Infinity });
  }
  // Same order as the board's hero row (VillageBoard.vue's heroGroups: each
  // group's level-1 cost, cheapest first; ties keep stack order). Bug report
  // -P2GRXPKDog09S-e30K6 (Matt): the level-up dialog listed them "in a
  // different order than... on the main board... which I think is by price."
  return candidates.sort((a, b) => a.cost - b.cost).map((c) => c.topInstanceId);
}

// Core hero-leveling mechanic, shared by the standard Village-phase level-up
// (villageActions.levelUpHero: pay XP, new copy -> discard) and several
// bespoke card effects that bend those defaults (Whetmage: new copy -> hand;
// Necrophidius/Veteran Trainer: free, no XP cost). Cannot skip levels; the
// target level's stack must have a remaining copy.
//
// heroInstanceId's own def.group determines the target stack for every
// named hero (Level 1->2->3 always stays within one group). The Regular
// basic card has no group of its own - it can become any currently
// available level-1 hero, so the caller must say which one via
// targetInstanceId (see getRegularLevelUpCandidates).
//
// Returns the new state, or throws if the level-up isn't legal.
// Caverns of Bane, Seedbomb (village-seedbomb): "React: When you level up a
// hero, destroy this card to put the lower-level hero in your discard pile
// instead of destroying it." An opt-in reaction, threaded through as
// useSeedbombInstanceId - only villageActions.js's levelUpHero (the main
// player-facing LEVEL_UP_HERO action) resolves a real player choice for
// this, matching the "reasonable default, real choice where timing allows"
// convention; every other performLevelUp caller (Whetmage's bulk
// auto-level-up, Veteran Trainer, Necrophidius's Trophy, ascendHeroToLevel4)
// simply doesn't pass it, so those bonus/derived level-up paths behave as
// before (old card destroyed as normal) rather than threading an extra
// interactive prompt through each of them - a deliberate, documented scope
// limit on this pass, not an oversight.
// xpTokenColors: bug report -Oxqycc8OLie0qvxFSJ2 (Matt): "the user ought to
// be able to select which XP pieces they want to discard" when leveling up -
// see effectHelpers.js's discardXpTokens/grantXp for the actual mechanism.
// Only villageActions.js's levelUpHero (the main player-facing action)
// resolves a real player choice for this (choices.xpTokenColors), matching
// useSeedbombInstanceId's own scope note just above - every other
// performLevelUp caller (Whetmage, Veteran Trainer, Necrophidius,
// ascendHeroToLevel4) doesn't pass it, so those keep the old auto-pick.
export function performLevelUp(state, playerId, heroInstanceId, {
  payXp = true, destination = 'discard', targetInstanceId, allowAscendToLevel4 = false, xpCostOverride, useSeedbombInstanceId,
  keepOldHeroInHand = false, xpTokenColors,
} = {}) {
  const player = state.players.find((p) => p.id === playerId);
  const def = getInstanceDefinition(state, heroInstanceId);
  if (!isHeroLike(def)) throw new Error('That card is not a hero.');
  if (useSeedbombInstanceId) {
    if (!player.hand.includes(useSeedbombInstanceId)) throw new Error('Seedbomb is not in your hand.');
    if (getInstanceDefinition(state, useSeedbombInstanceId).id !== 'village-seedbomb') {
      throw new Error('That card is not Seedbomb.');
    }
  }
  // Into the Abyss groups DO go to level 4 (every other set's heroes cap at
  // 3), but not through this normal path - see ascendHeroToLevel4 below.
  // allowAscendToLevel4 is that function's own explicit opt-in; every other
  // caller (the standard Village LEVEL_UP_HERO action, Whetmage, Veteran
  // Trainer, Necrophidius, ...) is blocked from taking a level-3 ItA hero to
  // level 4 this way.
  if (def.level === 3 && hasLevel4(def.group) && !allowAscendToLevel4) {
    throw new Error('This hero can only reach level 4 by winning a combat and paying 4 XP as a Spoils ability, not through the normal level-up action.');
  }
  // Matt: "a hero that has already been leveled up this turn can't be
  // leveled up a second time" - checked against this specific card
  // instance, not its hero group/type, so a different copy of the same
  // named hero is unaffected.
  if (player.turnFlags.leveledHeroInstanceIds.includes(heroInstanceId)) {
    throw new Error('You have already leveled up this hero this turn.');
  }

  // xpCostOverride: Into the Abyss's 3->4 ascension is a flat 4 XP (Matt: "you
  // can level up to 4 for 4xp"), independent of the card's own printed `xp`
  // field (level3/level4 ItA heroes print no `xp` value at all - only L1/L2
  // do, the normal source of XP a player earns, not a level-up cost).
  const xpCost = payXp ? (xpCostOverride ?? (def.xp || 0)) : 0;
  if (player.xp < xpCost) throw new Error('Not enough XP.');
  if (xpTokenColors && xpTokenColors.length > xpCost) {
    throw new Error(`Chose ${xpTokenColors.length} XP tokens to discard, but this level-up only costs ${xpCost}.`);
  }

  let group = def.group;
  let nextInstanceId;
  let nextIndex;
  let stack;

  if (group) {
    const nextLevel = def.level + 1;
    stack = state.village.heroStacks[group] || [];
    nextIndex = stack.findIndex((id) => getInstanceDefinition(state, id).level === nextLevel);
    if (nextIndex === -1) throw new Error('No higher-level copy of this hero remains in the village.');
    nextInstanceId = stack[nextIndex];
  } else {
    if (!targetInstanceId) throw new Error('Choose which level 1 hero to level this Regular into.');
    const targetDef = getInstanceDefinition(state, targetInstanceId);
    if (targetDef.level !== 1) throw new Error('A Regular can only level into a level 1 hero.');
    group = targetDef.group;
    stack = state.village.heroStacks[group] || [];
    nextIndex = stack.indexOf(targetInstanceId);
    if (nextIndex === -1) throw new Error('That hero is no longer available in the village.');
    nextInstanceId = targetInstanceId;
  }

  const newStack = [...stack.slice(0, nextIndex), ...stack.slice(nextIndex + 1)];

  // xpCost deduction goes through grantXp (not a raw `xp: p.xp - xpCost`)
  // so it stays in sync with Numenera's colored-XP tokens too (see
  // cards/effectHelpers.js's grantXp) - this is the only other place in the
  // engine that spent XP directly rather than through that shared choke point.
  let nextState = xpCost ? grantXp(state, playerId, -xpCost, { xpTokenColors }) : state;
  // Seedbomb destroyed BEFORE the hand-shuffling below reads its own hand
  // snapshot fresh - stats.cardsDestroyed + equippedWeapons cleanup via the
  // normal choke point, same as any other "destroy this card" effect.
  if (useSeedbombInstanceId) nextState = destroyFromHand(nextState, playerId, useSeedbombInstanceId);
  nextState = {
    ...nextState,
    players: nextState.players.map((p) => {
      if (p.id !== playerId) return p;
      const handWithoutOld = p.hand.filter((id) => id !== heroInstanceId);
      // Seedbomb's own "put the lower-level hero in your discard pile
      // instead of destroying it" - a fixed destination for the OLD card,
      // independent of `destination` above (which only ever controls where
      // the NEWLY leveled-into card lands).
      const discard = useSeedbombInstanceId ? [...p.discard, heroInstanceId] : p.discard;
      // Worlds Collide, Bluefire Preceptor (L4): "Instead of destroying the
      // hero you level with this ability, you may return it to your hand."
      // A THIRD disposal option for the OLD card (Seedbomb's own discard
      // above is the second; plain destruction, implicit, is the default) -
      // opted into per-use via keepOldHeroInHand, not a fixed card-level
      // behavior like Seedbomb's.
      const hand = destination === 'hand' ? [...handWithoutOld, nextInstanceId] : handWithoutOld;
      return {
        ...p,
        hand: keepOldHeroInHand ? [...hand, heroInstanceId] : hand,
        discard: destination === 'discard' ? [...discard, nextInstanceId] : discard,
        // The newly leveled-into card, not the old one - that's the card that
        // must not be leveled again this turn (see the check at the top).
        turnFlags: { ...p.turnFlags, leveledHeroInstanceIds: [...p.turnFlags.leveledHeroInstanceIds, nextInstanceId] },
        stats: { ...p.stats, heroesLeveledUp: p.stats.heroesLeveledUp + 1 },
      };
    }),
    village: { ...nextState.village, heroStacks: { ...nextState.village.heroStacks, [group]: newStack } },
  };
  return nextState;
}

// Into the Abyss, all 10 hero groups: "The level 4 heroes only have one copy
// each. The only way to level up from 3 to 4 is to win a combat with a level
// 3 in your hand. Then you can level up to 4 for 4xp as a spoils ability."
// (Matt.) Every level-3 ItA hero currently in hand right after a combat win
// is a legal target - not tied to any specific card's own printed Spoils
// ability text, so this is its own action (see actions/index.js's
// ASCEND_HERO, dispatched the same way TAKE_FAMILIAR is: gated on
// meta.spoilsWindowOpen, not on a particular hand card).
export function getAscendToLevel4Candidates(state, playerId) {
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return [];
  return player.hand.filter((id) => {
    const def = getInstanceDefinition(state, id);
    return isHeroLike(def) && def.level === 3 && hasLevel4(def.group);
  });
}

// xpTokenColors: same real player choice as levelUpHero's own
// choices.xpTokenColors (bug report -Oxqycc8OLie0qvxFSJ2) - this is just as
// much a direct, player-facing "pay XP to level up" action as that one, so
// it gets the same opt-in rather than being left on the old auto-pick.
export function ascendHeroToLevel4(state, playerId, heroInstanceId, { xpTokenColors } = {}) {
  const candidates = getAscendToLevel4Candidates(state, playerId);
  if (!candidates.includes(heroInstanceId)) {
    throw new Error('That hero is not eligible to ascend to level 4 right now.');
  }
  return performLevelUp(state, playerId, heroInstanceId, {
    payXp: true, destination: 'discard', allowAscendToLevel4: true, xpCostOverride: 4, xpTokenColors,
  });
}
